import React from 'react'
import { AppContent, AppSidebar, AppFooter, AppHeader } from '../components/index'
import { loadAxiosConfig, ThemeAxios } from '../utils/axios'
import { setValue as updateJWT } from '../redux/features/jwt/jwtSlice'
import {
  selectCurrentUser,
  setValue as updateCurrentUser,
} from '../redux/features/currentUser/currentUserSlice'
import {
  selectActiveBusinessEntity,
  setValue as updateActiveBusinessEntity,
} from '../redux/features/activeBusinessEntity/activeBusinessEntitySlice'
import { Redirect } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../redux/hooks'
import {
  selectGlobalConfiguration,
  setValue as updateGlobalConfiguration,
} from '../redux/features/globalConfiguration/globalConfigurationSlice'
import AppSidebarModern from '../components/AppSidebarModern'
import AppFloatingButtons from '../components/AppFloatingButtons'

const DefaultLayout = () => {
  const dispatch = useAppDispatch()
  const currentUser = useAppSelector(selectCurrentUser)
  const themeAxios = new ThemeAxios(
    null,
    false,
    false,
    currentUser?.active_language || null,
  ).getAxios()
  const activeBusinessEntity = useAppSelector(selectActiveBusinessEntity)
  const jwt = typeof window !== 'undefined' ? localStorage.getItem('jwt') : ''
  const globalConfiguration = useAppSelector(selectGlobalConfiguration)

  const getUserLoginResource = () => {
    if (currentUser === null) {
      if (jwt) {
        let status = 'pending'
        let result

        let fetchUserMe = themeAxios.get(
          `/users/myProfile`,
          loadAxiosConfig(jwt, activeBusinessEntity?.id || 0),
        )
        let suspender = fetchUserMe.then(
          (response) => {
            const { data } = response

            dispatch(updateJWT(jwt ? jwt : '')) // jwt always not null
            dispatch(updateCurrentUser(data))

            let activeBusinessEntitiesStringFromLS = localStorage.getItem('activeBusinessEntity')
            let activeBusinessEntitiesObjectFromLS = JSON.parse(activeBusinessEntitiesStringFromLS)
            if (
              activeBusinessEntitiesStringFromLS === 'undefined' ||
              !data.businessEntities.data.some(
                (be) => be.id === (activeBusinessEntitiesObjectFromLS?.id || 0),
              ) ||
              !data.businessEntities.data.some(
                (be) => be.title === (activeBusinessEntitiesObjectFromLS?.title || ''),
              ) ||
              !data.businessEntities.data.some(
                (be) => (be?.logo?.id || 0) === (activeBusinessEntitiesObjectFromLS?.logo?.id || 0),
              )
            ) {
              localStorage.removeItem('activeBusinessEntity')
              activeBusinessEntitiesStringFromLS = null
            }

            let setReduxActiveBE = false
            if (activeBusinessEntitiesStringFromLS) {
              const targetActiveBE = data.businessEntities.data.find(
                (be) => be.id === (activeBusinessEntitiesObjectFromLS?.id || 0),
              )
              if (targetActiveBE && targetActiveBE.publishedAt) {
                dispatch(updateActiveBusinessEntity(targetActiveBE))
                setReduxActiveBE = true
              }
            }

            if (data.businessEntities.data.length > 0) {
              if (!setReduxActiveBE) {
                const publishedBEs = data.businessEntities.data
                  .filter((be) => be.creator && currentUser && be.creator.id === currentUser.id)
                  .filter((be) => be.publishedAt)
                if (publishedBEs.length) {
                  dispatch(updateActiveBusinessEntity(publishedBEs[0]))
                  localStorage.setItem('activeBusinessEntity', JSON.stringify(publishedBEs[0]))
                }
              }

              localStorage.setItem(
                'allBusinessEntities',
                JSON.stringify(data.businessEntities.data),
              )
            }

            themeAxios
              .get(
                `/global-configuration?populate=*`,
                loadAxiosConfig(jwt, activeBusinessEntity?.id || 0),
              )
              .then((globalConfiguration) => {
                dispatch(updateGlobalConfiguration(globalConfiguration.data.data.attributes))
                if (typeof window !== 'undefined')
                  localStorage.setItem(
                    'globalConfiguration',
                    JSON.stringify(globalConfiguration.data.data.attributes),
                  )
              })

            // return read params
            status = 'success'
            result = null
          },
          (error) => {
            // Handle error. (invalid jwt)
            if (typeof window !== 'undefined') {
              localStorage.removeItem('jwt')
              localStorage.removeItem('activeBusinessEntity')
              localStorage.removeItem('allBusinessEntities')
            }

            dispatch(updateJWT(''))
            dispatch(updateCurrentUser(null))

            // return read params
            status = 'error'
            result = <Redirect to="/login" />
          },
        )

        return {
          read() {
            if (status === 'pending') {
              throw suspender
            } else if (status === 'error') {
              return result
            } else if (status === 'success') {
              return result
            }
          },
        }
      } else {
        // user did not login, no need to call /users/me api
        return {
          read() {
            return <Redirect to="/login" />
          },
        }
      }
    } else {
      // login-ed user
      return {
        read() {
          return null
        },
      }
    }
  }

  const userLoginResource = getUserLoginResource()
  const UserLoginLoader = () => {
    return userLoginResource.read()
  }

  const rtl = globalConfiguration.menu_mode === 'modern_rtl'
  const renderSideBar = (sideBarStyle) => {
    if (!globalConfiguration) return null

    switch (sideBarStyle) {
      case 'standard':
        return <AppSidebar showLogo={globalConfiguration.menu_with_logo !== false} />
      case 'modern':
        return (
          <AppSidebarModern
            menuDarkMode={globalConfiguration.menu_darkmode === true}
            showLogo={globalConfiguration.menu_with_logo !== false}
          />
        )
      case 'modern_rtl':
        return (
          <AppSidebarModern
            rtl={true}
            menuDarkMode={globalConfiguration.menu_darkmode === true}
            showLogo={globalConfiguration.menu_with_logo !== false}
          />
        )
      default:
        return <AppSidebar showLogo={globalConfiguration.menu_with_logo !== false} />
    }
  }

  return (
    <div>
      <UserLoginLoader />

      {currentUser && (
        <div style={{ display: 'flex' }}>
          {!rtl ? renderSideBar(globalConfiguration.menu_mode) : null}
          <div className="wrapper d-flex flex-column min-vh-100 bg-light">
            <AppHeader />
            <div className="body flex-grow-1 px-3">
              <AppContent />
            </div>
            <AppFooter />
          </div>
          {rtl ? renderSideBar(globalConfiguration.menu_mode) : null}
          <AppFloatingButtons />
        </div>
      )}
    </div>
  )
}

export default DefaultLayout
